import { action, computed, makeAutoObservable, makeObservable, observable } from 'mobx';
import _ from 'lodash';
import { convertKeysToSnake } from 'ziphy-web-shared/basic/api/myack/api.helpers';
import { checkCondition, interpolate, isAbsoluteEmpty } from 'ziphy-web-shared/basic/helpers';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { builderBlockVariants, builderButtonVariants, builderFieldVariants, builderGroupFieldVariants, builderTextVariants, builderTitleVariants, } from './';
import { getDefaultValue } from './ui/fields';
export class BuilderStore {
    constructor(data, form, options) {
        this.data = data;
        this.options = options;
        this.form = form;
        this.elements = new ElementsStore(this, this, this.data.template);
        makeAutoObservable(this);
    }
    // Computed
    get renderType() {
        return this.options.renderType;
    }
    get defaultFieldMode() {
        return this.options.defaultFieldMode;
    }
    get variables() {
        return this.data.variables || {};
    }
    getFieldsMap(elements, result = new Map()) {
        elements.forEach((item) => {
            if (item.config.element === 'field') {
                result.set(item.config.value, item);
            }
            else if (item.config.element === 'block') {
                this.getFieldsMap(item.elements.elements, result);
            }
            else if (item.config.element === 'groupField') {
                this.getFieldsMap(item.fields, result);
            }
        });
        return result;
    }
    get fieldsMap() {
        return this.getFieldsMap(this.elements.elements);
    }
    // Mutations
    SET_RENDER_TYPE(value) {
        this.options.renderType = value;
    }
    // Actions
    getPayloadValueByPath(path) {
        return _.get(this.data.payload, path);
    }
    getFormValueByPath(path) {
        return _.get(this.form.__private__.fields, path);
    }
    // Other
    get variants() {
        var _a, _b, _c, _d, _e, _f;
        return {
            title: Object.assign(Object.assign({}, builderTitleVariants), (((_a = this.options.custom) === null || _a === void 0 ? void 0 : _a.title) || {})),
            text: Object.assign(Object.assign({}, builderTextVariants), (((_b = this.options.custom) === null || _b === void 0 ? void 0 : _b.text) || {})),
            block: Object.assign(Object.assign({}, builderBlockVariants), (((_c = this.options.custom) === null || _c === void 0 ? void 0 : _c.block) || {})),
            button: Object.assign(Object.assign({}, builderButtonVariants), (((_d = this.options.custom) === null || _d === void 0 ? void 0 : _d.block) || {})),
            groupField: Object.assign(Object.assign({}, builderGroupFieldVariants), (((_e = this.options.custom) === null || _e === void 0 ? void 0 : _e.groupField) || {})),
            field: Object.assign(Object.assign({}, builderFieldVariants), (((_f = this.options.custom) === null || _f === void 0 ? void 0 : _f.field) || {})),
        };
    }
}
export class BaseElementStore {
    constructor(parent, builder, config) {
        this.parent = parent;
        this.builder = builder;
        this.config = config;
        // nothing change during app run
        // add if something will change in the future
        makeObservable(this, {});
    }
    get renderType() {
        return this.builder.renderType;
    }
    get renderParams() {
        var _a;
        return ((_a = this.config.params) === null || _a === void 0 ? void 0 : _a[this.renderType]) || {};
    }
    get variantConfig() {
        return (this.builder.variants[this.config.element][this.config.variant] ||
            this.builder.variants[this.config.element]['default']);
    }
    get targets() {
        return {
            variables: this.builder.variables,
            payload: this.builder.form.outputValues(),
            isSubmitted: this.builder.form.isSubmitted,
        };
    }
    get show() {
        var _a, _b;
        const condition = (_b = (_a = this.config.params) === null || _a === void 0 ? void 0 : _a[this.renderType]) === null || _b === void 0 ? void 0 : _b.show;
        if (typeof condition === 'boolean')
            return !!condition;
        if (typeof condition === 'undefined')
            return true;
        return checkCondition(this.targets, condition);
    }
}
export class TitleStore extends BaseElementStore {
    constructor(parent, builder, config) {
        super(parent, builder, config);
        makeObservable(this, {});
    }
}
export class TextStore extends BaseElementStore {
    constructor(parent, builder, config) {
        super(parent, builder, config);
        makeObservable(this, {});
    }
}
export class BlockStore extends BaseElementStore {
    constructor(parent, builder, config) {
        super(parent, builder, config);
        this._isEmpty = null;
        this.elements = new ElementsStore(this, this.builder, this.config.items);
        makeObservable(this, {
            _isEmpty: observable,
            elements: observable,
            isEmpty: computed,
            SET_IS_EMPTY: action,
        });
    }
    // Computed
    get isEmpty() {
        if (this._isEmpty !== null)
            return this._isEmpty;
        const elements = (this.elements.elements || []);
        if (elements.length === 0)
            return true;
        return elements.every((element) => {
            if (element.config.element === 'field' || element.config.element === 'groupField') {
                return element.isEmpty;
            }
            else {
                return element._isEmpty;
            }
        });
    }
    // Mutations
    SET_IS_EMPTY(value) {
        this._isEmpty = value;
    }
}
export class ButtonStore extends BaseElementStore {
    constructor(parent, builder, config) {
        super(parent, builder, config);
        makeObservable(this, { show: computed, runAction: action });
    }
    // not observable
    getFullEventName(event) {
        return `${this.builder.form.id}_${event}`;
    }
    get listenEventName() {
        return this.config.listenEventName
            ? this.getFullEventName(this.config.listenEventName)
            : this.config.listenEventName;
    }
    // Computed
    // Mutations
    // Actions
    runAction() {
        const action = this.config.action;
        switch (action.type) {
            case 'replace_payload':
                const payload = convertKeysToSnake(action.payload);
                const targets = Object.keys(payload);
                targets.forEach((target) => {
                    const field = this.builder.fieldsMap.get(target);
                    if (field) {
                        const result = interpolate(payload[target], this.builder.variables);
                        field.field.onChange(result);
                    }
                });
                break;
            case 'fire_event':
                const events = Array.isArray(action.event) ? action.event : [action.event];
                for (const event of events) {
                    $loader.simpleTrigger(this.getFullEventName(event));
                }
                break;
        }
    }
}
export class GroupFieldStore extends BaseElementStore {
    constructor(parent, builder, config) {
        super(parent, builder, config);
        this._isEmpty = null;
        if (!this.fields.length) {
            this.controller.add();
        }
        makeObservable(this, {
            _isEmpty: observable,
            fieldConfig: computed,
            fieldVariantConfig: computed,
            controller: computed,
            fields: computed,
            fieldsWithValues: computed,
            isEmpty: computed,
            SET_IS_EMPTY: action,
        });
    }
    // Computed
    get fieldConfig() {
        return this.config.field;
    }
    get fieldVariantConfig() {
        return this.builder.variants[this.config.field.element][`${this.config.field.variant}_${this.config.field.subvariant || 'default'}`];
    }
    get controller() {
        var _a, _b, _c, _d, _e;
        return this.builder.form.group(this.fieldConfig.value, getDefaultValue(this.fieldConfig.defaultValue, (_b = (_a = this.fieldVariantConfig) === null || _a === void 0 ? void 0 : _a.elementProps) === null || _b === void 0 ? void 0 : _b.defaultValue), {
            fieldConfig: Object.assign(Object.assign({}, (((_d = (_c = this.fieldVariantConfig) === null || _c === void 0 ? void 0 : _c.elementProps) === null || _d === void 0 ? void 0 : _d.fieldConfig) || {})), (((_e = this.fieldConfig) === null || _e === void 0 ? void 0 : _e.fieldConfig) || {})),
            type: 'field',
        });
    }
    get fields() {
        return this.controller.value.map((formField) => {
            return new FieldStore(this, this.builder, this.fieldConfig, formField);
        });
    }
    get fieldsWithValues() {
        return this.fields.filter((field) => !field.isEmpty);
    }
    get isEmpty() {
        if (this._isEmpty !== null)
            return this._isEmpty;
        return this.fields.every((field) => field.isEmpty);
    }
    // Mutations
    SET_IS_EMPTY(value) {
        this._isEmpty = value;
    }
}
export class FieldStore extends BaseElementStore {
    constructor(parent, builder, config, field) {
        super(parent, builder, config);
        this._isEmpty = null;
        this.readonly = false;
        this._field = field;
        makeObservable(this, {
            _field: observable,
            _isEmpty: observable,
            readonly: observable,
            variantConfig: computed,
            defaultFieldMode: computed,
            blocked: computed,
            isEmpty: computed,
            isReadonly: computed,
            isDeletable: computed,
            field: computed,
            SET_READ_ONLY: action,
            SET_IS_EMPTY: action,
        });
    }
    // Computed
    get variantConfig() {
        return this.builder.variants[this.config.element][`${this.config.variant}_${this.config.subvariant || 'default'}`];
    }
    get placeholder() {
        if ('placeholder' in this.config && typeof this.config.placeholder === 'string') {
            return interpolate(this.config.placeholder, this.builder.variables);
        }
        return '';
    }
    get defaultFieldMode() {
        return this.builder.defaultFieldMode;
    }
    get blocked() {
        var _a, _b;
        return Boolean((_b = (_a = this.field) === null || _a === void 0 ? void 0 : _a.primaryValue) === null || _b === void 0 ? void 0 : _b.blocked);
    }
    get isEmpty() {
        if (this._isEmpty !== null)
            return this._isEmpty;
        return isAbsoluteEmpty(this.field.outputValues());
    }
    get isReadonly() {
        return this.readonly || this.blocked;
    }
    get isDeletable() {
        return !this.isReadonly;
    }
    get field() {
        var _a, _b, _c, _d;
        if (this._field)
            return this._field;
        let defaultValue = getDefaultValue(this.config.defaultValue, (_b = (_a = this.variantConfig) === null || _a === void 0 ? void 0 : _a.elementProps) === null || _b === void 0 ? void 0 : _b.defaultValue);
        if (!this.config.defaultValue && Array.isArray(this.config.usedKeys)) {
            defaultValue = {};
            this.config.usedKeys.forEach((key) => {
                defaultValue[key] = null;
            });
        }
        return this.builder.form.field(this.config.value, defaultValue, Object.assign(Object.assign(Object.assign({}, (((_d = (_c = this.variantConfig) === null || _c === void 0 ? void 0 : _c.elementProps) === null || _d === void 0 ? void 0 : _d.fieldConfig) || {})), (this.config.fieldConfig || {})), { definitionSelectId: 'selectId' in this.config ? this.config.selectId : undefined }));
    }
    // Mutations
    SET_READ_ONLY(value) {
        this.readonly = value;
    }
    SET_IS_EMPTY(value) {
        this._isEmpty = value;
    }
}
export class ElementsStore {
    constructor(parent, builder, config) {
        this.parent = parent;
        this.builder = builder;
        this.config = config;
        this._elements = Array.isArray(this.config)
            ? this.config.map((item) => {
                const StoreClass = ElementsStore.elementConstructors[item.element];
                return StoreClass ? new StoreClass(this, this.builder, item) : null;
            })
            : [];
        makeAutoObservable(this);
    }
    // Computed
    get renderType() {
        return this.builder.renderType;
    }
    get elements() {
        return this._elements.filter((item) => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.variantConfig) === null || _a === void 0 ? void 0 : _a.Component) && (item === null || item === void 0 ? void 0 : item.show); });
    }
}
// Mutations
// Actions
// Other
ElementsStore.elementConstructors = {
    title: TitleStore,
    text: TextStore,
    block: BlockStore,
    button: ButtonStore,
    groupField: GroupFieldStore,
    field: FieldStore,
};
